import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useEffect, useReducer, useState } from 'react';
import getProfessionalSellersCountriesAllowed from '@/api/getProfessionalSellersCountriesAllowed';
import { AllowedCountriesState, AllowedCountriesAction } from '@/types/context';
import getUserData_v2 from '@/api/getUserData_v2';
import { UserDTO } from '@/types/other';

const initialState: AllowedCountriesState = {
  countries: [],
};

const defaultDispatch: React.Dispatch<AllowedCountriesAction> = () => {};

export const AllowedCountriesContext = React.createContext<{
  state: AllowedCountriesState;
  dispatch: React.Dispatch<AllowedCountriesAction>;
}>({
  state: initialState,
  dispatch: defaultDispatch,
});

const allowedCountriesReducer = (
  state: AllowedCountriesState,
  action: AllowedCountriesAction
): AllowedCountriesState => {
  switch (action.type) {
    case 'replace':
      return { ...state, countries: action.countries };
    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
};

export const AllowedCountriesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery<UserDTO | null>({
    queryKey: ['user_v2'],
    queryFn: getUserData_v2,
  });
  const {
    data: professionalSellersCountriesAllowed,
    isLoading: isLoadingProfessionalSellersCountriesAllowed,
  } = useQuery({
    queryKey: ['professionalSellersCountriesAllowed', user?.email],
    queryFn: getProfessionalSellersCountriesAllowed,
    enabled: Boolean(user?.id && !isLoading),
  });
  const [state, dispatch] = useReducer(allowedCountriesReducer, initialState);

  useEffect(() => {
    if (
      ((user && !isLoading) || user) &&
      professionalSellersCountriesAllowed &&
      !isLoadingProfessionalSellersCountriesAllowed
    ) {
      const userToUse = user || user;
      const allowedCountries = professionalSellersCountriesAllowed.countries.names.canBuyProf;
      if (userToUse?.isProffesional || userToUse?.specialProffesional) {
        if (userToUse.allowedCountriesType === 'SELL_ALL') {
          dispatch({
            type: 'replace',
            countries: professionalSellersCountriesAllowed.countries.names.canBuyProf,
          });
        } else {
          const userCountries = Array.isArray(userToUse.AllowedCountries)
            ? userToUse.AllowedCountries.map(country => {
                return country.Country.iso2;
              })
            : [];
          const countries =
            userCountries && userCountries.length > 0
              ? allowedCountries.filter(country => userCountries.includes(country.iso2))
              : allowedCountries;
          if (countries.length > 0) {
            dispatch({ type: 'replace', countries: countries });
          } else {
            dispatch({ type: 'replace', countries: allowedCountries });
          }
        }
      } else {
        dispatch({ type: 'replace', countries: allowedCountries });
      }
    }
  }, [
    user,
    isLoading,
    professionalSellersCountriesAllowed,
    isLoadingProfessionalSellersCountriesAllowed,
  ]);

  return (
    <AllowedCountriesContext.Provider value={{ state, dispatch }}>
      {children}
    </AllowedCountriesContext.Provider>
  );
};
